import React from 'react';
import {
  distanceUnit, speedUnit, elevationUnit, paceUnit,
  miToKm, ftToM, minPerMiToMinPerKm, KM_PER_MI,
} from '../utils/units';
import { METRIC_TERMS } from '../data/enduranceMetrics';

/**
 * IntervalList — unit-aware renderer for a structured endurance prescription.
 *
 * Every interval speaks in real, per-activity metrics. Distance/speed convert to
 * the profile's units on display; each metric spells its term out in full on the
 * big session card and abbreviates it on the compact selection card (the
 * learnable-jargon rule — see data/enduranceMetrics.js). No coded effort scales
 * (RPE/Zone/watts) ever render — intensity is carried by distance + speed/pace.
 *
 * Convertible / display-aware fields (canonical units in parens):
 *   distanceMi (mi) · distanceMiMax · open      → mi / km   (run/walk/hike/bike)
 *   distanceM  (m)  · distanceMMax  · open      → m         (rowing / swimming)
 *   paceMph (mph) · paceMphMax                  → mph / kph (bike/elliptical/treadmill)
 *   paceMinPerMi (min/mi) · paceMinPerMiMax     → /mi / /km (run / walk / hike)
 *   splitSec (s per 500 m)                      → m:ss /500m (rowing)
 *   paceSec100 (s per 100 m)                    → m:ss /100m (swimming)
 *   elevationFt (ft) · elevationFtMax · open    → ft / m gain
 *   inclinePct                                  → % incline (treadmill)
 *   level · levelMax                            → resistance level (bike/elliptical)
 *   cadence · cadenceMax · cadenceUnit          → spm / rpm  (strokes|strides|rev)
 *   label · effort (plain word) · cue           → free text
 *
 * A repeat block is { repeat: N, steps: [interval, ...] }.
 * Static library workouts and AI-generated sessions share this one renderer.
 */

const fmtNum = (x) => {
  const r = Math.round(x * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

// seconds (or fractional minutes×60) → "m:ss"
const clock = (sec) => {
  const t = Math.max(0, Math.round(sec));
  const m = Math.floor(t / 60);
  return `${m}:${String(t % 60).padStart(2, '0')}`;
};

// Term rendering: full form on the big card, abbreviation on the compact card.
const term = (key, compact) => {
  const t = METRIC_TERMS[key];
  if (!t) return '';
  return compact ? t.abbr : t.full;
};

function summarize(iv, units, compact) {
  if (iv.repeat && Array.isArray(iv.steps)) {
    const inner = iv.steps.map((s) => summarize(s, units, compact)).join(' + ');
    return `${iv.repeat}× (${inner})`;
  }

  const metric = units === 'metric';
  const toKmIfMetric = (mi) => (metric ? miToKm(mi) : mi);
  const paceIfMetric = (mph) => (metric ? mph * KM_PER_MI : mph);
  const elevIfMetric = (ft) => (metric ? ftToM(ft) : ft);

  // ---- Leading quantity: timing + distance ----
  const qty = [];
  if (iv.durationMin != null) qty.push(`${iv.durationMin} min`);
  else if (iv.durationSec != null) qty.push(`${iv.durationSec}s`);

  if (iv.distanceMi != null) {
    const lo = fmtNum(toKmIfMetric(iv.distanceMi));
    const hi = iv.distanceMiMax != null ? fmtNum(toKmIfMetric(iv.distanceMiMax)) : null;
    const val = hi ? `${lo}–${hi}` : `${lo}${iv.open ? '+' : ''}`;
    qty.push(`${val} ${distanceUnit(units)}`);
  }
  if (iv.distanceM != null) {
    const hi = iv.distanceMMax != null ? `–${iv.distanceMMax}` : (iv.open ? '+' : '');
    qty.push(`${iv.distanceM}${hi} m`);
  }

  let s = qty.join(' ');
  if (iv.label) s = s ? `${s} ${iv.label}` : iv.label;

  // ---- Pace / speed (one per interval) — attaches with "@" ----
  let pace = null;
  if (iv.paceMph != null) {
    const lo = fmtNum(paceIfMetric(iv.paceMph));
    const hi = iv.paceMphMax != null ? fmtNum(paceIfMetric(iv.paceMphMax)) : null;
    pace = `${hi ? `${lo}–${hi}` : lo} ${speedUnit(units)}`;
  } else if (iv.paceMinPerMi != null) {
    const conv = (m) => clock((metric ? minPerMiToMinPerKm(m) : m) * 60);
    const lo = conv(iv.paceMinPerMi);
    const hi = iv.paceMinPerMiMax != null ? conv(iv.paceMinPerMiMax) : null;
    pace = `${hi ? `${lo}–${hi}` : lo} ${paceUnit(units)}`;
  } else if (iv.splitSec != null) {
    pace = `${clock(iv.splitSec)} ${term('split', compact)}`;
  } else if (iv.paceSec100 != null) {
    pace = `${clock(iv.paceSec100)} ${term('pace100', compact)}`;
  }
  if (pace) s += ` @ ${pace}`;

  // ---- Everything else — joined with " · " ----
  const extras = [];

  if (iv.inclinePct != null) {
    const hi = iv.inclinePctMax != null ? `–${iv.inclinePctMax}` : '';
    extras.push(`${iv.inclinePct}${hi}% incline`);
  }
  if (iv.level != null) {
    const hi = iv.levelMax != null ? `–${iv.levelMax}` : '';
    extras.push(`${term('level', compact)} ${iv.level}${hi}`);
  }
  if (iv.cadence != null && iv.cadenceUnit) {
    const hi = iv.cadenceMax != null ? `–${iv.cadenceMax}` : '';
    extras.push(`${iv.cadence}${hi} ${term(iv.cadenceUnit, compact)}`);
  }
  if (iv.elevationFt != null) {
    const lo = Math.round(elevIfMetric(iv.elevationFt));
    const hi = iv.elevationFtMax != null ? Math.round(elevIfMetric(iv.elevationFtMax)) : null;
    const val = hi ? `${lo}–${hi}` : `${lo}${iv.open ? '+' : ''}`;
    extras.push(`${val} ${elevationUnit(units)} gain`);
  }
  if (iv.effort) extras.push(iv.effort);
  if (iv.cue) extras.push(iv.cue);

  if (extras.length) s += ` · ${extras.join(' · ')}`;
  return s.trim();
}

export default function IntervalList({ intervals, units = 'imperial', compact = false }) {
  if (!Array.isArray(intervals) || intervals.length === 0) return null;

  if (compact) {
    return (
      <div className="text-xs text-enf-text-secondary leading-relaxed">
        {intervals.map((iv) => summarize(iv, units, true)).join('  →  ')}
      </div>
    );
  }

  return (
    <ul className="space-y-1.5">
      {intervals.map((iv, i) => (
        <li key={i} className="flex gap-2 text-enf-text-secondary">
          <span className="text-enf-accent-fg mt-0.5 shrink-0">›</span>
          <span>{summarize(iv, units, false)}</span>
        </li>
      ))}
    </ul>
  );
}
